import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  CheckCircle2, Circle, Lock, ChevronRight, Shield, FileText, 
  Car, Image, DollarSign, ClipboardList, CreditCard, Rocket,
  X, Clock, Sparkles, TrendingUp, Zap, AlertTriangle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping for checklist items
const iconMap = {
  'shield': Shield,
  'file-text': FileText,
  'car': Car,
  'image': Image,
  'dollar-sign': DollarSign,
  'clipboard-list': ClipboardList,
  'credit-card': CreditCard,
  'rocket': Rocket,
  'check-circle': Rocket
};

// Individual checklist item row with actionable button
const ChecklistItem = ({ item, onNavigate }) => {
  const Icon = iconMap[item.icon] || Circle;
  
  const statusConfig = {
    complete: {
      badge: 'Done',
      badgeClass: 'bg-green-100 text-green-700 border-green-200',
      icon: CheckCircle2,
      iconClass: 'text-green-500',
      rowClass: 'bg-green-50/50 border-green-100'
    },
    pending: {
      badge: null,
      badgeClass: '',
      icon: Circle,
      iconClass: 'text-slate-300',
      rowClass: 'hover:bg-slate-50 border-transparent hover:border-slate-100'
    },
    blocked: {
      badge: item.blocked_reason === 'payouts_required' ? 'Payouts Required' : 'Blocked',
      badgeClass: item.blocked_reason === 'payouts_required' 
        ? 'bg-amber-100 text-amber-700 border-amber-200' 
        : 'bg-slate-100 text-slate-500 border-slate-200',
      icon: item.blocked_reason === 'payouts_required' ? AlertTriangle : Lock,
      iconClass: item.blocked_reason === 'payouts_required' ? 'text-amber-500' : 'text-slate-300',
      rowClass: item.blocked_reason === 'payouts_required' 
        ? 'bg-amber-50/50 border-amber-100 cursor-pointer hover:bg-amber-50'
        : 'opacity-60 cursor-not-allowed border-transparent'
    }
  };
  
  const config = statusConfig[item.status] || statusConfig.pending;
  const StatusIcon = config.icon;
  
  const handleClick = () => {
    // Allow navigation for payouts_required blocked state (to go to payouts page)
    if (item.status !== 'blocked' || item.blocked_reason === 'payouts_required') {
      onNavigate(item.link);
    }
  };

  return (
    <div 
      className={`flex items-center gap-3 p-3 sm:p-4 rounded-lg border transition-all ${
        item.status !== 'blocked' || item.blocked_reason === 'payouts_required' ? 'cursor-pointer' : ''
      } ${config.rowClass}`}
      onClick={handleClick}
      data-testid={`checklist-item-${item.id}`}
    >
      {/* Step Number */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
        {item.step_number || '•'}
      </div>
      
      {/* Status Icon */}
      <div className="flex-shrink-0">
        <StatusIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${config.iconClass}`} />
      </div>
      
      {/* Item Icon */}
      <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm sm:text-base ${
          item.status === 'complete' ? 'text-green-700' : 
          item.blocked_reason === 'payouts_required' ? 'text-amber-800' : 'text-slate-900'
        }`}>
          {item.title}
        </p>
        <p className={`text-xs sm:text-sm truncate ${
          item.blocked_reason === 'payouts_required' ? 'text-amber-600' : 'text-slate-500'
        }`}>
          {item.description}
        </p>
      </div>
      
      {/* Action Button or Badge */}
      <div className="flex-shrink-0">
        {item.status === 'complete' ? (
          <Badge className={`text-xs ${config.badgeClass}`}>{config.badge}</Badge>
        ) : item.status === 'blocked' && item.blocked_reason !== 'payouts_required' ? (
          <Badge className={`text-xs ${config.badgeClass}`}>{config.badge}</Badge>
        ) : (
          <Button 
            size="sm" 
            variant={item.blocked_reason === 'payouts_required' ? 'default' : 'ghost'}
            className={`h-8 px-2 sm:px-3 ${
              item.blocked_reason === 'payouts_required' 
                ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs'
                : 'text-[#004B8D] hover:bg-blue-50'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(item.link);
            }}
            data-testid={`checklist-action-${item.id}`}
          >
            <span className="hidden sm:inline mr-1 text-xs">
              {item.action_button || 'Complete'}
            </span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Success card shown when onboarding is complete
const OnboardingSuccessCard = ({ summary, onNavigate }) => {
  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50/50" data-testid="onboarding-success-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-base sm:text-lg text-green-800">Your Listing is Live!</CardTitle>
            <CardDescription className="text-green-600">You're all set to receive bookings</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
          <div className="text-center p-2 sm:p-3 bg-white/70 rounded-lg">
            <p className="text-lg sm:text-2xl font-bold text-green-700">{summary?.active_vehicle_count || 0}</p>
            <p className="text-xs text-slate-500">Live Vehicles</p>
          </div>
          <div className="text-center p-2 sm:p-3 bg-white/70 rounded-lg">
            <p className="text-lg sm:text-2xl font-bold text-green-700">
              {summary?.payouts_status === 'active' ? '✓' : '—'}
            </p>
            <p className="text-xs text-slate-500">Payouts Ready</p>
          </div>
          <div className="text-center p-2 sm:p-3 bg-white/70 rounded-lg">
            <div className="flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
            </div>
            <p className="text-xs text-slate-500">Instant Book</p>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <Button 
            size="sm" 
            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 h-9"
            onClick={() => onNavigate('/host/vehicles')}
          >
            <Car className="w-4 h-4 mr-1.5" />
            View Vehicles
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="flex-1 sm:flex-none border-green-300 text-green-700 hover:bg-green-50 h-9"
            onClick={() => onNavigate('/host/bookings')}
          >
            <TrendingUp className="w-4 h-4 mr-1.5" />
            View Bookings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main onboarding checklist card
export const OnboardingChecklist = ({ onDismiss }) => {
  const { api, isHost } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState(null);
  const [snoozed, setSnoozed] = useState(false);

  useEffect(() => {
    if (isHost()) {
      fetchOnboardingStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/users/host-onboarding-status');
      setOnboardingData(response.data);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (link) => {
    navigate(link);
  };

  const handleSnooze = async () => {
    try {
      await api.post('/users/host-onboarding-dismiss', { action: 'snooze' });
      setSnoozed(true);
      toast.success('Checklist snoozed for 24 hours');
      if (onDismiss) onDismiss();
    } catch (error) {
      toast.error('Failed to snooze checklist');
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-100">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't show anything if no data, snoozed, or already completed permanently
  if (!onboardingData || snoozed) {
    return null;
  }

  // If already completed permanently (one-time system), don't show checklist
  if (onboardingData.already_completed) {
    return null;
  }

  // Show success card if complete (will be marked as permanently complete on backend)
  if (onboardingData.is_complete) {
    return <OnboardingSuccessCard summary={onboardingData.summary} onNavigate={handleNavigate} />;
  }

  const { checklist, progress, payouts_required_for_publish } = onboardingData;

  return (
    <Card className="border-[#004B8D]/20 bg-gradient-to-br from-blue-50/50 to-slate-50" data-testid="onboarding-checklist-card">
      <CardHeader className="pb-2 sm:pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#004B8D]/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-[#004B8D]" />
            </div>
            <div>
              <CardTitle className="text-base sm:text-lg">Complete Your Host Setup</CardTitle>
              <CardDescription className="text-sm">Finish these steps to start receiving bookings</CardDescription>
            </div>
          </div>
          {!onboardingData.can_dismiss ? null : (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-slate-600"
              onClick={handleSnooze}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">
              {progress.completed} of {progress.total} complete
            </span>
            <span className="text-sm font-medium text-[#004B8D]">{progress.percentage}%</span>
          </div>
          <Progress value={progress.percentage} className="h-2" />
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Alert if payouts required for publish */}
        {payouts_required_for_publish && (
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Payouts required to publish</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Set up payouts to receive payments before your listing can go live.
              </p>
            </div>
          </div>
        )}
        
        {/* Pro tip */}
        {!payouts_required_for_publish && (
          <div className="mb-4 p-3 bg-white/60 rounded-lg border border-slate-100">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-[#004B8D]">Pro tip:</span> Listings with at least 3 clear photos receive significantly more booking requests.
            </p>
          </div>
        )}
        
        {/* Checklist Items */}
        <div className="space-y-2">
          {checklist.map((item) => (
            <ChecklistItem 
              key={item.id} 
              item={item} 
              onNavigate={handleNavigate}
            />
          ))}
        </div>
        
        {/* Snooze Button */}
        {!onboardingData.can_dismiss && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-slate-500 hover:text-slate-700 h-9"
              onClick={handleSnooze}
            >
              <Clock className="w-4 h-4 mr-2" />
              Remind me later
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Reminder bar for other host pages
export const OnboardingReminderBar = () => {
  const { api, isHost } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isHost()) {
      checkOnboardingStatus();
    }
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await api.get('/users/host-onboarding-status');
      // Don't show if already completed permanently or currently complete
      if (!response.data.is_complete && !response.data.already_completed) {
        setProgress(response.data.progress);
        setVisible(true);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  if (!visible || !progress || dismissed) {
    return null;
  }

  const stepsRemaining = progress.total - progress.completed;

  return (
    <div 
      className="bg-[#004B8D] text-white px-4 py-2.5 flex items-center justify-between"
      data-testid="onboarding-reminder-bar"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Rocket className="w-4 h-4 flex-shrink-0" />
        <span className="text-sm font-medium truncate">
          Complete your host setup to start receiving bookings
        </span>
        <Badge variant="secondary" className="bg-white/20 text-white text-xs flex-shrink-0">
          {stepsRemaining} step{stepsRemaining !== 1 ? 's' : ''} left
        </Badge>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <Button 
          size="sm" 
          variant="secondary" 
          className="h-7 text-xs bg-white text-[#004B8D] hover:bg-slate-100"
          onClick={() => navigate('/host')}
          data-testid="continue-setup-btn"
        >
          Continue Setup
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-white/70 hover:text-white hover:bg-white/10"
          onClick={handleDismiss}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default OnboardingChecklist;
